import React from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../../components/ui/dialog';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import CustomSelect from '../../../components/ui/CustomSelect';
import { formatRupiah, parseRupiah } from './utils/formatRupiah';

interface Department {
  id: number;
  name: string;
}

interface Position {
  id: number;
  name: string;
}

interface RecruitmentForm {
  department_id: string | number;
  position_id: string | number;
  number_of_positions: number;
  employment_type: string;
  justification: string;
  requirements: string;
  expected_start_date: string;
  budget: string;
}

interface RecruitmentFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: any;
  form: RecruitmentForm;
  setForm: (form: RecruitmentForm) => void;
  departments: Department[];
  positions: Position[];
  saving: boolean;
  onSave: () => void;
}

export default function RecruitmentFormDialog({
  open,
  onOpenChange,
  editing,
  form,
  setForm,
  departments,
  positions,
  saving,
  onSave,
}: RecruitmentFormDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editing ? 'Edit Rekrutmen Karyawan' : 'Ajukan Rekrutmen Karyawan'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Divisi *</Label>
              <CustomSelect
                value={
                  form.department_id && form.department_id !== '' && form.department_id !== null
                    ? String(form.department_id)
                    : ''
                }
                onChange={v => {
                  if (v && v !== '') {
                    setForm({ ...form, department_id: Number(v) });
                  } else {
                    setForm({ ...form, department_id: '' });
                  }
                }}
                options={
                  departments && Array.isArray(departments) && departments.length > 0
                    ? departments.map(dept => ({ value: String(dept.id), label: dept.name }))
                    : [{ value: '', label: 'Tidak ada divisi' }]
                }
                placeholder="Pilih divisi"
                width="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label>Posisi *</Label>
              <CustomSelect
                value={
                  form.position_id && form.position_id !== '' && form.position_id !== null
                    ? String(form.position_id)
                    : ''
                }
                onChange={v => {
                  if (v && v !== '') {
                    setForm({ ...form, position_id: Number(v) });
                  } else {
                    setForm({ ...form, position_id: '' });
                  }
                }}
                options={
                  positions && Array.isArray(positions) && positions.length > 0
                    ? positions.map(pos => ({ value: String(pos.id), label: pos.name }))
                    : [{ value: '', label: 'Tidak ada posisi' }]
                }
                placeholder="Pilih posisi"
                width="w-full"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Jumlah Posisi</Label>
              <Input
                type="number"
                min="1"
                value={form.number_of_positions}
                onChange={e =>
                  setForm({ ...form, number_of_positions: parseInt(e.target.value) || 1 })
                }
              />
            </div>

            <div className="space-y-2">
              <Label>Tipe Kepegawaian</Label>
              <CustomSelect
                value={form.employment_type}
                onChange={v => setForm({ ...form, employment_type: v })}
                options={[
                  { value: 'permanent', label: 'Tetap' },
                  { value: 'contract', label: 'Kontrak' },
                  { value: 'intern', label: 'Magang' },
                ]}
                placeholder="Pilih tipe kepegawaian"
                width="w-full"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Justifikasi</Label>
            <textarea
              value={form.justification}
              onChange={e => setForm({ ...form, justification: e.target.value })}
              placeholder="Jelaskan alasan kebutuhan rekrutmen..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
          </div>

          <div className="space-y-2">
            <Label>Persyaratan</Label>
            <textarea
              value={form.requirements}
              onChange={e => setForm({ ...form, requirements: e.target.value })}
              placeholder="Kualifikasi yang dibutuhkan..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Target Mulai Kerja</Label>
              <Input
                type="date"
                value={form.expected_start_date}
                onChange={e => setForm({ ...form, expected_start_date: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Budget (Rp)</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">Rp</span>
                <Input
                  type="text"
                  value={form.budget ? formatRupiah(form.budget) : ''}
                  onChange={e => {
                    const rawValue = parseRupiah(e.target.value);
                    setForm({ ...form, budget: rawValue });
                  }}
                  placeholder="0"
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </div>
        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Batal
          </Button>
          <Button
            onClick={onSave}
            disabled={saving || !form.department_id || !form.position_id}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {editing ? 'Simpan' : 'Ajukan'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
